import axios from 'axios';
import { openAlert } from '../Util/Util';
import { extractControllerFromUrl, extractFunnelPageIdFromUrl } from '../Util/URLBuilder';

/**
 * Hook to provide form submission functionality
 * @returns {Object} Object containing submitForm and getFormData functions
 */
export const useFormSubmission = () => {
    // Function to get form data as a string
    const getFormData = () => {
        const form = document.getElementById('reg-form');
        if (!form) {
            console.error('Form not found');
            return '';
        }
        const formData = new FormData(form);
        return new URLSearchParams(formData).toString();
    };

    return {
        submitForm,
        getFormData
    };
};

/**
 * Creates and attaches the global checkForm function
 * @param {Function} submitFormImpl - The form submission implementation function
 * @returns {Function} The original checkForm function if it existed
 */
export const createGlobalCheckForm = (submitFormImpl) => {
    // Store original function if it exists
    const originalCheckForm = window.checkForm;
    
    // Create new global function
    window.checkForm = async (formDataStr, setButtonIndicator, openAlertFn, ajaxUrl, enablePayment, submitBtn) => {
        try {
            // Extract form data parameters
            const form = document.getElementById('reg-form');
            
            // Create options object with proper URL parsing
            const options = {
                enablePayment,
                openAlert: openAlertFn,
                ajaxUrl, // Pass the complete URL directly
                // Extract controller and funnel_page_id from the URL
                controller: extractControllerFromUrl(ajaxUrl),
                funnel_page_id: extractFunnelPageIdFromUrl(ajaxUrl)
            };
            
            
            // Call implementation
            return await submitFormImpl(submitBtn, form, options);
        } catch (error) {
            console.error('Error in checkForm wrapper:', error);
            // Reset button state
            if (setButtonIndicator) {
                setButtonIndicator('off');
            }
            // Show error
            if (openAlertFn) {
                openAlertFn('系統發生錯誤，請稍後再試。');
            }
            return { success: false, error };
        }
    };
    
    return originalCheckForm;
};

/**
 * HookEvents-specific form submission implementation
 * This matches the structure expected in index.js
 * 
 * @param {object} submitButton - The submit button element
 * @param {object} form - The form element
 * @param {object} options - Additional options like funnel_page_id, turnstileToken, etc.
 * @returns {Promise} - Promise that resolves with submission result
 */
export const submitForm = async (submitButton, form, options = {}) => {
    // Extract options with defaults to handle undefined values
    const { 
        funnel_page_id, 
        turnstileToken, 
        ajaxUrl 
    } = options;
    
    // Create a default t function if none is provided
    const defaultT = key => key; // Identity function that returns the key unchanged
    const t = typeof options.t === 'function' ? options.t : defaultT;
    const optionsOpenAlert = options.openAlert;
    
    // Use the translation function properly
    const translate = (key, defaultText, variables = {}) => {
        try {
            return t(key, variables);
        } catch (e) {
            // If t throws an error, fall back to default text with variable replacement
            console.warn('Translation function error:', e);
            let fallbackText = defaultText || key;
            
            // Simple variable replacement for fallback
            Object.keys(variables).forEach(varKey => {
                const placeholder = `{{${varKey}}}`;
                fallbackText = fallbackText.replace(new RegExp(placeholder, 'g'), variables[varKey]);
            });
            
            return fallbackText;
        }
    };
    
    // Use a consistent alert function
    const showAlert = (message) => {
        try {
            // First try the options-provided openAlert
            if (typeof optionsOpenAlert === 'function') {
                optionsOpenAlert(message);
                return;
            }
            
            // Then try window.openAlert
            if (typeof window.openAlert === 'function') {
                // Pass the message directly without translation - 
                // window.openAlert is responsible for translation
                window.openAlert(message, t);
                return;
            }
            
            // Then try SweetAlert if available
            if (typeof Swal !== 'undefined') {
                // Use our translation helper with fallback
                const displayMessage = translate(message, message);
                Swal.fire({
                    title: '',
                    text: displayMessage,
                    icon: 'warning',
                    confirmButtonText: translate('OK', 'OK')
                });
                return;
            }
            
            // Fallback to browser alert if no alert function is available
            console.warn('No alert function available, using native alert:', message);
            alert(message);
        } catch (alertError) {
            console.error('Error showing alert:', alertError);
            // Final fallback if everything else fails
            try {
                alert(message);
            } catch (e) {
                console.error('Even native alert failed:', e);
            }
        }
    };
    
    try {
        // Show loading indicator
        if (submitButton) {
            submitButton.setAttribute('data-kt-indicator', 'on');
            submitButton.disabled = true;
        }
        
        // Get form data - collect from both form and React components
        let formData = new FormData(form);

        // First, collect all page_user_hook_session data before removing hidden fields
        const pageUserHookSessionData = new Map();
        
        // Try multiple selectors to find all page_user_hook_session inputs
        const selectors = [
            'input[name^="page_user_hook_session["]',
            'select[name^="page_user_hook_session["]',
            'textarea[name^="page_user_hook_session["]',
            'input[name*="page_user_hook_session"]',
            'select[name*="page_user_hook_session"]',
            'textarea[name*="page_user_hook_session"]'
        ];
        
        const allPageUserHookSessionInputs = [];
        selectors.forEach(selector => {
            try {
                const elements = document.querySelectorAll(selector);
                elements.forEach(el => {
                    if (el.name && el.name.includes('page_user_hook_session') && !allPageUserHookSessionInputs.includes(el)) {
                        allPageUserHookSessionInputs.push(el);
                    }
                });
            } catch (e) {
                // Silently ignore selector errors
            }
        });
        
        if (allPageUserHookSessionInputs.length === 0) {
            const allInputs = document.querySelectorAll('input, select, textarea');
            allInputs.forEach(input => {
                if (input.name && input.name.includes('page_user_hook_session')) {
                    allPageUserHookSessionInputs.push(input);
                }
            });
        }
        
        allPageUserHookSessionInputs.forEach(element => {
            if (element.name) {
                // Handle different input types
                let value = '';
                if (element.type === 'radio' || element.type === 'checkbox') {
                    if (element.checked) {
                        value = element.value || (element.type === 'checkbox' ? '1' : '');
                    } else {
                        return; // Skip unchecked radio/checkbox
                    }
                } else if (element.tagName === 'SELECT') {
                    value = element.value || '';
                } else {
                    // For hidden inputs and text inputs, get the value directly
                    value = element.value || '';
                }
                
                // For qty fields, only keep the maximum value (to handle PC/mobile duplicate inputs)
                // This ensures we don't lose data when there are multiple inputs with the same name
                if (element.name.includes('[qty]')) {
                    if (!pageUserHookSessionData.has(element.name)) {
                        pageUserHookSessionData.set(element.name, []);
                    }
                    const currentValues = pageUserHookSessionData.get(element.name);
                    const qtyValue = parseInt(value, 10) || 0;
                    // If this is a qty field, use the maximum value instead of all values
                    // This handles cases where PC and mobile versions both have inputs
                    if (currentValues.length === 0 || qtyValue > (parseInt(currentValues[0], 10) || 0)) {
                        pageUserHookSessionData.set(element.name, [qtyValue.toString()]);
                    }
                } else {
                    // Store all values for fields with the same name (for arrays)
                    if (!pageUserHookSessionData.has(element.name)) {
                        pageUserHookSessionData.set(element.name, []);
                    }
                    pageUserHookSessionData.get(element.name).push(value);
                }
            }
        });

        // Remove all hidden fields from FormData (except _csrfToken)
        // Note: We will add page_user_hook_session data separately later to ensure we have the latest values
        const formDataEntries = Array.from(formData.entries());
        formData = new FormData(); // Create new FormData
        
        formDataEntries.forEach(([name, value]) => {
            // Skip page_user_hook_session fields - we'll add them separately with latest values
            if (name.startsWith('page_user_hook_session[')) {
                return; // Skip, will be added later
            }
            
            const honeypotField = window.__form_honeypot_field__ || '';
            if (name === '_csrfToken' || name === 'form_loaded_at' || name.startsWith('_hp_') || (honeypotField && name === honeypotField)) {
                formData.append(name, value);
                return;
            }
            
            // Check if this is a hidden field by looking at the DOM
            // Try to find the input element in form or document
            let input = form.querySelector(`[name="${name}"]`);
            if (!input) {
                input = document.querySelector(`[name="${name}"]`);
            }
            
            // Only include if it's not a hidden input
            if (!input || input.type !== 'hidden') {
                formData.append(name, value);
            }
        });

        // Also collect visible inputs from form-inputs and document
        const formInputs = document.getElementById('form-inputs');
        if (formInputs) {
            const reactInputs = formInputs.querySelectorAll('input:not([type="hidden"]), select, textarea');
            reactInputs.forEach(input => {
                if (input.name && !input.name.startsWith('page_user_hook_session[') && !formData.has(input.name)) {
                    if (input.type === 'radio' || input.type === 'checkbox') {
                        if (input.checked) {
                            formData.append(input.name, input.value || (input.type === 'checkbox' ? '1' : ''));
                        }
                    } else {
                        formData.append(input.name, input.value || '');
                    }
                }
            });
        }

        // Collect visible inputs from entire document (excluding hidden and page_user_hook_session)
        const allVisibleInputs = document.querySelectorAll('input:not([type="hidden"]), select, textarea');
        allVisibleInputs.forEach(input => {
            if (input.name && 
                !input.name.startsWith('page_user_hook_session[') && 
                input.name !== '_csrfToken' &&
                !formData.has(input.name)) {
                if (input.type === 'radio' || input.type === 'checkbox') {
                    if (input.checked) {
                        formData.append(input.name, input.value || (input.type === 'checkbox' ? '1' : ''));
                    }
                } else {
                    formData.append(input.name, input.value || '');
                }
            }
        });

        // Now add all page_user_hook_session data back (overwrite any existing entries)
        pageUserHookSessionData.forEach((values, name) => {
            // Remove all existing entries for this name first
            while (formData.has(name)) {
                formData.delete(name);
            }
            // Add all values (for array fields like hook_session_fees[xxx][qty])
            values.forEach(value => {
                // Only add non-empty values, except for qty which can be 0
                const isQtyField = name.includes('[qty]');
                // Always add the value (even if empty) for page_user_hook_session fields
                // The server will handle validation
                formData.append(name, value);
            });
        });
        
        // Ensure _csrfToken is included
        const csrfInput = document.querySelector('input[name="_csrfToken"]');
        let csrfToken = null;
        if (csrfInput && csrfInput.value) {
            if (!formData.has('_csrfToken')) {
                formData.append('_csrfToken', csrfInput.value);
            }
            csrfToken = csrfInput.value;
        }

        let formDataStr = new URLSearchParams(formData).toString();
        if (turnstileToken) {
            axios.defaults.headers.common['X-Turnstile-Token'] = turnstileToken;
            const params = new URLSearchParams(formDataStr);
            if (!params.has('cf-turnstile-response')) {
                params.set('cf-turnstile-response', turnstileToken);
            }
            formDataStr = params.toString();
        }
        
        // Helper function to check if a field is mandatory
        const isFieldMandatory = (fieldElement) => {
            if (!fieldElement) return false;
            
            // Check HTML5 required attribute
            if (fieldElement.hasAttribute('required')) {
                return true;
            }
            
            // Check if parent has required class
            const parentGroup = fieldElement.closest('.form-group');
            if (parentGroup && parentGroup.classList.contains('required')) {
                return true;
            }
            
            // Check FormValidation.io notEmpty validator
            const fv = window.store?.getState?.()?.fv || (typeof FormValidation !== 'undefined' && form ? FormValidation.formValidation(form) : null);
            if (fv && fieldElement.name) {
                const field = fv.elements?.[fieldElement.name];
                if (field && field.validators && field.validators.notEmpty) {
                    return true;
                }
            }
            
            return false;
        };
        
        // Validate required selections first - session checking ("場次 checking")
        const hookSessionElements = document.querySelectorAll('.hook-session-id:checked');
        const allHookSessionElements = document.querySelectorAll('.hook-session-id');
        const hasHookSessions = allHookSessionElements.length > 0;
        const hasHiddenSessionSelect = document.getElementById('hidden-session-select');
        
        
        // Only check session selection for HookEvents if it's mandatory
        if (hasHookSessions && hookSessionElements.length === 0) {
            // Check if any session field is mandatory
            const isSessionMandatory = Array.from(allHookSessionElements).some(field => isFieldMandatory(field));
            if (isSessionMandatory) {
                if (submitButton) {
                    submitButton.setAttribute('data-kt-indicator', 'off');
                    submitButton.disabled = false;
                }
                const errorMessage = translate('請選擇場次', '請選擇場次');
                showAlert(errorMessage);
                
                // Create a proper error with the message
                const error = new Error(errorMessage);
                error.code = 'SESSION_REQUIRED';
                throw error;
            }
        }
        
        if (!hasHookSessions && !hasHiddenSessionSelect) {
            if (submitButton) {
                submitButton.setAttribute('data-kt-indicator', 'off');
                submitButton.disabled = false;
            }
            const errorMessage = translate('沒有有效場次或名額已滿，請聯絡管理人員。', '沒有有效場次或名額已滿，請聯絡管理人員。');
            showAlert(errorMessage);
            
            // Create a proper error with the message
            const error = new Error(errorMessage);
            error.code = 'NO_VALID_SESSIONS';
            throw error;
        }
        
        // Check if payment is enabled for this form
        const enablePayment = options.enablePayment || document.querySelectorAll('.qty').length > 0;
        if (enablePayment) {
			// Sum all selected quantities and validate against limits
			let totalQty = 0;
			let maxParticipants = 0;
			const qtySelects = document.querySelectorAll('.qty');
			qtySelects.forEach(select => {
				const val = parseInt(select.value, 10);
				if (!isNaN(val)) {
					totalQty += val;
				}
				const maxAttr = parseInt(select.getAttribute('data-max-participants'), 10);
				if (!isNaN(maxAttr)) {
					maxParticipants = Math.max(maxParticipants, maxAttr);
				}
			});

			// Also check hidden quantity inputs for React components
			const qtyInputs = document.querySelectorAll('input.qty-hidden[name*="page_user_hook_session[hook_session_fees]"][name*="[qty]"]');
			if (qtyInputs.length > 0 && totalQty === 0) {
				qtyInputs.forEach(input => {
					const val = parseInt(input.value, 10);
					if (!isNaN(val)) {
						totalQty += val;
					}
				});
			}

			// If there are quantity inputs, require at least 1 ticket
			const requiresAtLeastOne = qtyInputs.length > 0;

			if (totalQty === 0 || (maxParticipants > 0 && totalQty > maxParticipants)) {
				if (submitButton) {
					submitButton.setAttribute('data-kt-indicator', 'off');
					submitButton.disabled = false;
				}
				const errorMessage = totalQty === 0
					? translate('請選擇至少 1 張門票數量', '請選擇至少 1 張門票數量')
					: translate('最多可選購 {{0}} 張門票', '最多可選購 {{0}} 張門票', {0: maxParticipants});
				showAlert(errorMessage);
				const error = new Error(errorMessage);
				error.code = totalQty === 0 ? 'QTY_TOTAL_ZERO' : 'QTY_TOTAL_EXCEEDS_LIMIT';
				throw error;
			}

        }
        
        // Check fee selection validation - ensure selected fees match quantities
        // Note: page_user_hook_session[hook_session_fees] data is already collected and will be submitted
        // regardless of validation result (data collection happens before validation at lines 164-306)
        const feeSelectionInputs = document.querySelectorAll('select[name*="hook_session_fee_selection"]');
        
        // Check if there are any friend inputs - if no friends, skip fee selection validation
        // Data submission is not affected - page_user_hook_session[hook_session_fees] will still be passed
        const friendInputs = document.querySelectorAll('input[name*="friend["], .friend-input-group');
        const hasFriends = friendInputs.length > 0;
        
        // Skip fee selection validation if no friends
        // This only skips validation, data submission (page_user_hook_session[hook_session_fees]) still happens
        if (!hasFriends) {
            // No friends, skip fee selection validation
            // page_user_hook_session[hook_session_fees] data will still be submitted (collected at lines 203-226, added to FormData at lines 292-306)
        } else {
			// Get session fees with quantities from original selects
			const getSessionFeesWithQuantities = () => {
				const fees = [];
				const feeInputs = document.querySelectorAll('input.qty-hidden[name*="page_user_hook_session[hook_session_fees]"][name*="[qty]"]');
				feeInputs.forEach(input => {
					const qty = parseInt(input.value, 10);
					if (!isNaN(qty) && qty > 0) {
						const feeId = input.name.match(/\[hook_session_fees\]\[(.*?)\]\[qty\]/)?.[1];
						if (feeId) {
							fees.push({ id: feeId, qty: qty });
						}
					}
				});
				return fees;
			};
			
			// Calculate expected fee count from session fee quantities
			const getTotalQuantity = () => {
				let totalQty = 0;
				
				// Try multiple selectors to find quantity selects
				const qtySelects1 = document.querySelectorAll('.qty');
				const qtyInputs = document.querySelectorAll('input.qty-hidden[name*="page_user_hook_session[hook_session_fees]"][name*="[qty]"]');
				
				// Method 1: Use .qty selects
				qtySelects1.forEach(select => {
					const val = parseInt(select.value, 10);
					if (!isNaN(val)) {
						totalQty += val;
					}
				});
				
				// Method 3: If still no results, use hidden inputs
				if (totalQty === 0 && qtyInputs.length > 0) {
					qtyInputs.forEach(input => {
						const val = parseInt(input.value, 10);
						if (!isNaN(val)) {
							totalQty += val;
						}
					});
				}
				
				return totalQty;
			};
			
			const expectedFeeCount = getTotalQuantity();
			const actualFeeCount = feeSelectionInputs.length;
			
			// Check if all required fee selections are made
			const allSelectionsMade = Array.from(feeSelectionInputs).every(select => select.value !== '');
			
			// Detailed fee type validation
			if (expectedFeeCount > 0 && actualFeeCount === expectedFeeCount && allSelectionsMade) {
				const expectedFees = getSessionFeesWithQuantities();
				
				const expectedFeeIdList = [];
				expectedFees.forEach(fee => {
					for (let i = 0; i < fee.qty; i++) {
						expectedFeeIdList.push(fee.id);
					}
				});
				expectedFeeIdList.sort();

				const selectedFeeIdList = Array.from(feeSelectionInputs).map(select => select.value);
				selectedFeeIdList.sort();

				// Check if fee types match exactly
				if (JSON.stringify(expectedFeeIdList) !== JSON.stringify(selectedFeeIdList)) {
					if (submitButton) {
						submitButton.setAttribute('data-kt-indicator', 'off');
						submitButton.disabled = false;
					}
					const errorMessage = translate('錯誤：參加者所選票種數量未能配合。', '錯誤：參加者所選票種數量未能配合。');
					showAlert(errorMessage);
					const error = new Error(errorMessage);
					error.code = 'FEE_TYPE_MISMATCH';
					throw error;
				}
			}
			
			if (expectedFeeCount > 0 && (actualFeeCount !== expectedFeeCount || !allSelectionsMade)) {
				if (submitButton) {
					submitButton.setAttribute('data-kt-indicator', 'off');
					submitButton.disabled = false;
				}
				const errorMessage = translate('請選擇正確數量的票種', '請選擇正確數量的票種');
				showAlert(errorMessage);
				const error = new Error(errorMessage);
				error.code = 'FEE_SELECTION_MISMATCH';
				throw error;
			}
        }
        
        // Always use the provided ajaxUrl directly if available
        // This ensures we use exactly the URL constructed by the button component
        const requestUrl = ajaxUrl || null;
        
        if (!requestUrl) {
            console.error('No AJAX URL provided for form submission');
            const errorMessage = translate('提交表單時發生錯誤，請重試。', '提交表單時發生錯誤，請重試。');
            showAlert(errorMessage);
            
            // Reset button state
            if (submitButton) {
                submitButton.setAttribute('data-kt-indicator', 'off');
                submitButton.disabled = false;
            }
            
            // Create a proper error with the message
            const error = new Error(errorMessage);
            error.code = 'URL_MISSING';
            throw error;
        }
        
        // Prepare headers
        const headers = {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Requested-With': 'XMLHttpRequest'
        };
        if (csrfToken) {
            headers['X-CSRF-Token'] = csrfToken;
        }
        
        const response = await axios.post(requestUrl, formDataStr, {
            headers: headers
        });
        
        const data = response.data;
        if (data.require_turnstile) {
            if (submitButton) {
                submitButton.setAttribute('data-kt-indicator', 'off');
                submitButton.disabled = false;
            }
            const errorMessage = data.rm || 'Security verification required. Please try again.';
            showAlert(errorMessage);
            return { success: false, require_turnstile: true, message: errorMessage };
        }
        // Process response
        if (data.rc === 1 || data.rc === true) {
            
            // Handle redirect if present
            if (data.redirect_url || data.redirect) {
                const redirectUrl = data.redirect_url || data.redirect;
                window.location.href = redirectUrl;
                return { success: true, redirected: true };
            }
            
            // Process HTML content replacement if present
            if (data.html_content) {
                const formContainer = form.parentElement;
                if (formContainer) {
                    formContainer.innerHTML = data.html_content;
                }
                return { success: true, replaced: true };
            }
            
            // Standard form submission
            if (form.getAttribute('action')) {
				const inIframe = (() => {
					try {
						return window.self !== window.top;
					} catch (e) {
						return true;
					}
				})();
				
				// In iframe mode, we cannot rely on native form.submit() to send custom headers.
				// Instead, post via axios with X-Iframe so backend can run iframe-mode CSRF/Turnstile logic.
				if (inIframe) {
					const actionUrl = form.getAttribute('action');
					headers['X-Iframe'] = '1';
					const resp = await axios.post(actionUrl, formDataStr, { headers: headers });
					const finalUrl = resp?.request?.responseURL;
					window.location.href = finalUrl || actionUrl;
					return { success: true, submitted: true, redirected: true };
				}

                // Before submitting, ensure all page_user_hook_session data is in the form as hidden inputs
                // This is needed because form.submit() only submits inputs that are inside the form element
                // Remove any existing page_user_hook_session hidden inputs from form (to avoid duplicates)
                const existingPageUserHookSessionInputs = form.querySelectorAll('input[name^="page_user_hook_session["], select[name^="page_user_hook_session["], textarea[name^="page_user_hook_session["]');
                existingPageUserHookSessionInputs.forEach(input => {
                    // Only remove if it's a hidden input we added, not if it's a select or textarea
                    if (input.type === 'hidden' || (input.tagName === 'INPUT' && input.type === 'hidden')) {
                        input.remove();
                    }
                });
                
                // Add all page_user_hook_session data as hidden inputs to the form
                pageUserHookSessionData.forEach((values, name) => {
                    values.forEach(value => {
                        const hiddenInput = document.createElement('input');
                        hiddenInput.type = 'hidden';
                        hiddenInput.name = name;
                        hiddenInput.value = value;
                        form.appendChild(hiddenInput);
                    });
                });
                
                // Also ensure _csrfToken is in the form
                if (csrfToken && !form.querySelector('input[name="_csrfToken"]')) {
                    const csrfInput = document.createElement('input');
                    csrfInput.type = 'hidden';
                    csrfInput.name = '_csrfToken';
                    csrfInput.value = csrfToken;
                    form.appendChild(csrfInput);
                }
                const needsTurnstile = options.turnstileRequired || window.__form_turnstile_required__;
                if (needsTurnstile && typeof window.__ensure_turnstile_token__ === 'function') {
                    try {
                        const ensuredToken = await window.__ensure_turnstile_token__();
                        if (ensuredToken && typeof ensuredToken === 'string' && ensuredToken.length > 10 && !ensuredToken.startsWith('TEMP_')) {
                            let turnstileInput = form.querySelector('input[name="cf-turnstile-response"]');
                            if (!turnstileInput) {
                                turnstileInput = document.createElement('input');
                                turnstileInput.type = 'hidden';
                                turnstileInput.name = 'cf-turnstile-response';
                                form.appendChild(turnstileInput);
                            }
                            turnstileInput.value = ensuredToken;
                            form.dataset.turnstileToken = ensuredToken;
                            form.dataset.turnstileTimestamp = Date.now().toString();
                        }
                    } catch (e) {
                    }
                } else if (turnstileToken) {
                    let turnstileInput = form.querySelector('input[name="cf-turnstile-response"]');
                    if (!turnstileInput) {
                        turnstileInput = document.createElement('input');
                        turnstileInput.type = 'hidden';
                        turnstileInput.name = 'cf-turnstile-response';
                        form.appendChild(turnstileInput);
                    }
                    turnstileInput.value = turnstileToken;
                }
                form.submit();
                return { success: true, submitted: true };
            }
            
            return { success: true };
        } else {
            // Form validation failed
            
            // Reset button state
            if (submitButton) {
                submitButton.setAttribute('data-kt-indicator', 'off');
                submitButton.disabled = false;
            }
            
            // Check if server returned updated HTML content (e.g., with error messages)
            if (data.html_content) {
                const formContainer = form.parentElement;
                if (formContainer) {
                    formContainer.innerHTML = data.html_content;
                }
                return { success: false, replaced: true };
            }
            
            // Get the exact error message from the server response
            let errorMessage = '表單驗證失敗';
            if (data.rm) {
                errorMessage = data.rm; // Use the exact message from the server
            }
            
            // Show error message directly in alert without translation
            showAlert(errorMessage);
            
            // Return error info
            return { success: false, message: errorMessage };
        }
    } catch (error) {
        console.error('HookEvents: Form submission error', error);
        
        // Reset button state
        if (submitButton) {
            submitButton.setAttribute('data-kt-indicator', 'off');
            submitButton.disabled = false;
        }
        
        // Show error message
        const errorMessage = translate('系統發生錯誤，請稍後再試。', '系統發生錯誤，請稍後再試。');
        showAlert(errorMessage);
        
        throw error; // Re-throw to ensure NonSE.js catches it for proper display
    }
};

// Export a default object as expected by index.js
export default {
    submitForm,
    useFormSubmission,
    createGlobalCheckForm
};
